import { Empty, Skeleton, Space, Tooltip, Typography } from "antd"
import { Proposal, ProposalJobData, calculateTotalPrice, findCurrentProposal, hasActiveAutomations, sortProposalsByVersion } from "./proposalsModel"
import { useProposalsInvalidator, useProposalSummaryInvalidator, useProposalsQuery, useProposalSummaryQuery, useCreatePreviewLinkMutation } from "./proposalsQueries"
import { useEffect, useState } from "react"
import ProposalSummaryCard from "./ProposalSummaryCard"
import ProposalVersionList from "./ProposalVersionList"
import { faFileInvoiceDollar } from "@fortawesome/pro-solid-svg-icons"
import IconAndTextButton from "../../IconAndTextButton"
import ManageProposalDrawer from '../../Proposals/ManageProposalDrawer'
import SendProposalDrawer from "../../Proposals/SendProposalDrawer"
import LinkCreatedModal from "../../Proposals/LinkedCreatedModal/LinkCreatedModal"

export type Props = {
  job: ProposalJobData
  onAutomationChange?: (active: boolean) => void
  onLinkCustomerToQbo?: () => void
  onLinkCostCodeToQbo?: (costCodeId: number) => void
}

const Proposals = ({ job, onAutomationChange, onLinkCustomerToQbo, onLinkCostCodeToQbo }: Props) => {
  const proposalsInvalidator = useProposalsInvalidator()
  const proposalSummaryInvalidator = useProposalSummaryInvalidator()
  const proposalsQuery = useProposalsQuery(job?.id)
  const summaryQuery = useProposalSummaryQuery(job?.id)
  const proposals: Proposal[] = proposalsQuery.data ?? []
  const sortedProposals = sortProposalsByVersion(proposals)
  const currentProposal = findCurrentProposal(proposals)

  const [creatingProposal, setCreatingProposal] = useState(false)
  const [proposalToSend, setProposalToSend] = useState<{ id: number, total: number } | undefined>()
  const [proposalLink, setProposalLink] = useState<string | undefined>()
  const [proposalToManage, setProposalToManage] = useState<Proposal | undefined>()

  const createPreviewLinkMutation = useCreatePreviewLinkMutation((link: string) => {
    setTimeout(() => window.open(link, '_blank'), 0)
  })

  const invalidateJob = () => {
    proposalsInvalidator.invalidate(job.id)
    proposalSummaryInvalidator.invalidate(job.id)
  }

  useEffect(() => {
    if (job !== undefined) {
      proposalsInvalidator.invalidate(job.id)
    }
  }, [job])

  useEffect(() => {
    if (currentProposal !== undefined) {
      onAutomationChange?.(hasActiveAutomations(currentProposal))
    }
  }, [currentProposal])

  if (proposalsQuery.isLoading) {
    return <Skeleton active loading={true} />
  }

  const customerIsActive = job?.customer?.isActive ?? false
  const hasProposals = sortedProposals.length > 0
  const createButtonText = hasProposals ? 'Draft New Version' : 'Create New Proposal'
  const createDisabled = !customerIsActive
  const createTooltip = customerIsActive
    ? createButtonText
    : 'Proposal creation is disabled due to the customer being inactive.'

  return <div>
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
      <Typography.Text style={{ fontSize: '18px', flexGrow: 1 }}>Proposals</Typography.Text>
      <Tooltip title={createTooltip}>
        <Space>
          <IconAndTextButton
            type="primary"
            size="large"
            onClick={() => setCreatingProposal(true)}
            icon={faFileInvoiceDollar}
            text={createButtonText}
            disabled={createDisabled}
          />
        </Space>
      </Tooltip>
    </div>

    {hasProposals && <ProposalSummaryCard
      summary={summaryQuery.data}
      loading={summaryQuery.isLoading}
    />}

    {hasProposals
      ? <ProposalVersionList
        proposals={sortedProposals}
        currentProposalId={currentProposal?.id}
        job={job}
        onInvalidateJob={invalidateJob}
        onViewLink={setProposalLink}
        onSend={proposal => setProposalToSend({ id: proposal.id, total: calculateTotalPrice(proposal.workareas ?? []) })}
        onPreview={proposal => createPreviewLinkMutation.mutate(proposal.id)}
        onManage={setProposalToManage}
      />
      : <Empty description="Ready to Win New Business? Get started by creating your first proposal." />}

    <ManageProposalDrawer
      proposalId={proposalToManage?.id}
      jobId={job?.id}
      open={proposalToManage !== undefined || creatingProposal}
      onClose={() => { setProposalToManage(undefined); setCreatingProposal(false) }}
      onSaved={invalidateJob}
      onLinkCustomerToQbo={onLinkCustomerToQbo}
      onLinkCostCodeToQbo={onLinkCostCodeToQbo}
      onPreview={proposalId => createPreviewLinkMutation.mutate(proposalId)}
      onSend={(proposalId, proposalTotal) => {
        setProposalToManage(undefined)
        setCreatingProposal(false)
        setProposalToSend({ id: proposalId, total: proposalTotal })
      }}
    />


    {proposalToSend !== undefined && <SendProposalDrawer
      open={true}
      job={job}
      proposalId={proposalToSend.id}
      proposalTotal={proposalToSend.total}
      onClose={() => setProposalToSend(undefined)}
    />}

    <LinkCreatedModal
      visible={proposalLink !== undefined}
      linkIsFromExistingProposal={true}
      link={proposalLink ?? ''}
      onClose={() => setProposalLink(undefined)}
    />
  </div>
}

export default Proposals
